import React from 'react';
import { useApp } from '../../context/Store';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import SafeIcon from '../../common/SafeIcon';
import { FiMessageSquare, FiCheckCircle, FiActivity, FiArrowRight } from 'react-icons/fi';

export default function Home() {
  const { currentMission } = useApp();

  return (
    <div className="max-w-6xl mx-auto p-8 space-y-8">
      <section>
        <h1 className="text-2xl font-semibold text-slate-900">Good morning.</h1>
        <p className="text-slate-600 mt-1 flex items-center gap-2">
          Current focus: <span className="font-medium text-slate-900">{currentMission}</span>
          <Badge variant="outcome" className="ml-2">On Track</Badge>
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Main Actions Area */}
        <div className="lg:col-span-2 space-y-6">
          <section>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Your perspective is needed</h2>
            <div className="space-y-3">
              <ActionCard 
                type="question"
                title="What are we doing that creates activity but not qualified demand?"
                reason="Your experience with Q1 MQL conversion is highly relevant."
                mission="Increase Q3 Qualified Pipeline"
                icon={FiMessageSquare}
                variant="insight"
              />
              <ActionCard 
                type="decision"
                title="Review: Shift paid budget from Search to LinkedIn Account Targeting"
                reason="You are listed as a reviewer for paid media strategy."
                mission="Increase Q3 Qualified Pipeline"
                icon={FiCheckCircle}
                variant="decision"
              />
              <ActionCard 
                type="experiment"
                title="Interpret: Landing Page Variant B conversion increase"
                reason="Experiment data collection is complete and requires human interpretation."
                mission="Reduce CAC by 15%"
                icon={FiActivity}
                variant="outcome"
              />
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Recent Organizational Learning</h2>
              <button className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                View all <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
               <Card className="p-4">
                  <Badge variant="outcome" className="mb-2">Confirmed Learning</Badge>
                  <p className="text-sm font-medium text-slate-900 mb-2">Technical buyers ignore gated content if the abstract lacks code examples.</p>
                  <p className="text-xs text-slate-500">From Exp-402 • Applied to 3 new missions</p>
               </Card>
               <Card className="p-4">
                  <Badge variant="risk" className="mb-2">Assumption Rejected</Badge>
                  <p className="text-sm font-medium text-slate-900 mb-2">Faster response times (under 5m) do not correlate with higher close rates in Enterprise.</p>
                  <p className="text-xs text-slate-500">From Decision-112 • Validated via CRM Data</p>
               </Card>
            </div>
          </section>
        </div>

        {/* Right Dashboard Panel */}
        <div className="space-y-6">
          <section>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Active Missions</h2>
            <div className="space-y-4">
              <Card className="p-4 border-l-4 border-l-blue-500">
                <h3 className="font-medium text-slate-900 text-sm">Increase Q3 Qualified Pipeline by 20%</h3>
                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                  <span>Phase: Deliberation</span>
                  <span className="text-blue-600 font-medium">3 Open Questions</span>
                </div>
              </Card>
              <Card className="p-4 border-l-4 border-l-emerald-500">
                <h3 className="font-medium text-slate-900 text-sm">Launch EMEA Market Strategy</h3>
                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                  <span>Phase: Execution</span>
                  <span className="text-emerald-600 font-medium">2 Active Exp.</span>
                </div>
              </Card>
            </div>
          </section>

          <section className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">AI Intelligence Brief</h2>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2">
                <SafeIcon icon={FiActivity} className="text-purple-500 mt-0.5" />
                <span className="text-slate-600">Detected a contradiction between recent customer signals and Assumption #42.</span>
              </li>
              <li className="flex gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-purple-500 mt-0.5" />
                <span className="text-slate-600">Content Agent has completed draft for EMEA Launch. Awaiting approval.</span>
              </li>
            </ul>
          </section>
        </div>

      </div>
    </div>
  );
}

function ActionCard({ type, title, reason, mission, icon, variant }) {
  return (
    <Card className="p-4 flex items-start gap-4 hover:border-slate-300 transition-colors">
      <div className={`p-2 rounded-md bg-${variant.split('-')[1] || 'slate'}-50 text-${variant.split('-')[1] || 'slate'}-600`}>
        <SafeIcon icon={icon} className="text-lg" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant={variant}>{type}</Badge>
          <span className="text-xs text-slate-500">{mission}</span>
        </div>
        <h3 className="text-slate-900 font-medium mb-1">{title}</h3>
        <p className="text-sm text-slate-600">{reason}</p>
      </div>
    </Card>
  );
}